import React from 'react';
import type { Project } from '../../types';
import { Button, Card, CardBody, InlineAlert, useToast } from '../../ui';

/**
 * The panelist entry link: the link a panelist opens to take the survey. The backend checks the panelist at entry
 * (campaign active and in its field period, not a duplicate, matches the target group, not in an excluded campaign,
 * a free quota cell) and then sends them to the survey URL. `[PANELIST_ID]` is replaced with the panelist's id;
 * adding `&tg=<target group id>` targets one target group.
 */
export function EntryLinkCard({ project }: { project: Project }) {
  const { notify } = useToast();
  const link = project.entryLink || '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      notify('Entry link copied', { tone: 'ok' });
    } catch (e) {
      notify('Could not copy. Select the link and copy it manually.', { tone: 'info' });
    }
  };

  return (
    <Card>
      <CardBody>
        <h2 className="text-base font-semibold text-ink">Panelist entry link</h2>
        <p className="mt-1 text-sm text-muted">Panelists open this link to take the survey. Replace <code className="rounded bg-chip px-1">[PANELIST_ID]</code> with the panelist's id.</p>
        {!link ? (
          <div className="mt-4"><InlineAlert tone="info">This campaign has no entry link yet. Older campaigns get theirs the next time they are saved: open “Edit details” and save.</InlineAlert></div>
        ) : (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <input readOnly aria-label="Entry link" value={link} onFocus={(e) => e.currentTarget.select()} className="min-w-0 flex-1 rounded-md border border-line bg-canvas px-2.5 py-1.5 font-mono text-xs text-ink" />
              <Button variant="secondary" size="sm" onClick={copy}>Copy</Button>
            </div>
            <p className="mt-2 text-xs text-muted">
              To send people to one target group only, add <code className="rounded bg-chip px-1">&amp;tg=&lt;target group id&gt;</code>.
              {project.surveyUrl ? <> Respondents who qualify are sent to the survey URL below.</> : <> No survey URL is set yet: add one in “Edit details” so qualified respondents have somewhere to go.</>}
            </p>
            {project.surveyUrl && <p className="mt-1 break-all text-xs text-muted">Survey URL: <span className="font-mono text-ink">{project.surveyUrl}</span></p>}
          </>
        )}
      </CardBody>
    </Card>
  );
}
